import { profileStorageKey } from './profileStorageKey';

const STORAGE_KEY_PREFIX = 'ofrontend.bookingProfileLocal';

export type LocalBookingProfileFields = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  birthDate: string;
  occupation: string;
};

function storageKey(loginId?: string | null): string {
  return profileStorageKey(STORAGE_KEY_PREFIX, loginId);
}

export function markLocalBookingProfileComplete(
  fields: LocalBookingProfileFields,
  loginId?: string | null,
): void {
  try {
    localStorage.setItem(storageKey(loginId), JSON.stringify(fields));
  } catch {
    /* ignore */
  }
}

export function readLocalBookingProfileComplete(loginId?: string | null): LocalBookingProfileFields | null {
  try {
    const raw = localStorage.getItem(storageKey(loginId));
    if (!raw?.trim()) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<LocalBookingProfileFields>;
    return {
      firstName: typeof parsed.firstName === 'string' ? parsed.firstName.trim() : '',
      lastName: typeof parsed.lastName === 'string' ? parsed.lastName.trim() : '',
      email: typeof parsed.email === 'string' ? parsed.email.trim() : '',
      phone: typeof parsed.phone === 'string' ? parsed.phone.trim() : '',
      birthDate: typeof parsed.birthDate === 'string' ? parsed.birthDate.trim() : '',
      occupation: typeof parsed.occupation === 'string' ? parsed.occupation.trim() : '',
    };
  } catch {
    return null;
  }
}

export function localBookingProfileWireSnapshot(loginId?: string | null): Record<string, string> | null {
  const saved = readLocalBookingProfileComplete(loginId);
  if (!saved) {
    return null;
  }
  const snapshot: Record<string, string> = {};
  if (saved.firstName) {
    snapshot.first_name = saved.firstName;
  }
  if (saved.lastName) {
    snapshot.last_name = saved.lastName;
  }
  if (saved.email) {
    snapshot.email = saved.email;
  }
  if (saved.phone) {
    snapshot.phone_number = saved.phone;
  }
  if (saved.birthDate) {
    snapshot.birth_date = saved.birthDate;
  }
  if (saved.occupation) {
    snapshot.occupation = saved.occupation;
  }
  return Object.keys(snapshot).length > 0 ? snapshot : null;
}

export function isLocalBookingProfileComplete(loginId?: string | null): boolean {
  const saved = readLocalBookingProfileComplete(loginId);
  if (!saved) {
    return false;
  }
  return Boolean(
    saved.firstName &&
      saved.lastName &&
      saved.email &&
      saved.phone &&
      saved.birthDate,
  );
}

function pickWireField(value: unknown, fallback: string): string {
  const next = typeof value === 'string' ? value.trim() : '';
  return next || fallback;
}

export function persistLocalBookingProfileFromWire(
  payload: {
    first_name?: unknown;
    last_name?: unknown;
    email?: unknown;
    phone_number?: unknown;
    birth_date?: unknown;
    occupation?: unknown;
  },
  loginId?: string | null,
): void {
  const existing = readLocalBookingProfileComplete(loginId);
  markLocalBookingProfileComplete(
    {
      firstName: pickWireField(payload.first_name, existing?.firstName ?? ''),
      lastName: pickWireField(payload.last_name, existing?.lastName ?? ''),
      email: pickWireField(payload.email, existing?.email ?? ''),
      phone: pickWireField(payload.phone_number, existing?.phone ?? ''),
      birthDate: pickWireField(payload.birth_date, existing?.birthDate ?? ''),
      occupation: pickWireField(payload.occupation, existing?.occupation ?? ''),
    },
    loginId,
  );
}

/** Keep the local snapshot aligned with GET /profile so stale phones/names cannot win. */
export function syncLocalBookingProfileFromSummary(
  summary: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phone: string | null;
    dateOfBirth: string | null;
    occupation: string | null;
  },
  loginId?: string | null,
): void {
  const existing = readLocalBookingProfileComplete(loginId);
  const next: LocalBookingProfileFields = {
    firstName: summary.firstName?.trim() || existing?.firstName || '',
    lastName: summary.lastName?.trim() || existing?.lastName || '',
    email: summary.email?.trim() || existing?.email || '',
    phone: summary.phone?.trim() || existing?.phone || '',
    birthDate: summary.dateOfBirth?.trim() || existing?.birthDate || '',
    occupation: summary.occupation?.trim() || existing?.occupation || '',
  };
  if (!next.firstName || !next.lastName || !next.email || !next.phone || !next.birthDate) {
    if (existing && summary.phone?.trim() && existing.phone !== summary.phone.trim()) {
      markLocalBookingProfileComplete({ ...existing, phone: summary.phone.trim() }, loginId);
    }
    return;
  }
  markLocalBookingProfileComplete(next, loginId);
}
